import React, { useEffect, useState } from 'react';
import TreeView from '@mui/lab/TreeView';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TreeItem from '@mui/lab/TreeItem';
import { FileItemProps } from '../FileItem/FileItem.component';
import { decodeFileName, findFile } from '@Helper/FolderHelper';
import './FileSystemNavigator.scss';

export type FileSystemNavigatorProps = {
  files: FileItemProps[];
  rootPathName: string;
  style?: React.CSSProperties;
  currentPath: string[];
  onPathSelected?: (paths: string[]) => void;
  filter?: (file: FileItemProps) => boolean;
};

const FileSystemNavigator = ({
  files,
  rootPathName,
  style = {
    height: 240,
    flexGrow: 1,
    width: 'auto',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  currentPath,
  onPathSelected,
  filter,
}: FileSystemNavigatorProps) => {
  const [defaultExpanded, setDefaultExpanded] = useState<string[]>([]);
  const [pathList, setPathList] = useState<string[]>(currentPath || []);
  const rootFile: FileItemProps = {
    name: rootPathName,
    id: 'root-path-1',
    files,
    isOdd: false,
    path: '/',
    handleOnPathClicked: (folder: FileItemProps) => {},
    actionMenuItems: [],
  };

  const generateNodeId = (file: FileItemProps, level: number): string => {
    console.log({ file, level });

    return `{ "level":${level}, "name": "${file?.name}", "id": "${
      file?.id
    }", "type": "${file?.type || 'file'}" }`;
  };

  const filterTreeItem = (file: FileItemProps) => {
    return !filter || filter(file);
  };

  useEffect(() => {
    let ids: string[] = [generateNodeId(rootFile, 1)];
    let paths: string[] = [];

    pathList
      .map(path => decodeFileName(path))
      .forEach((path, i) => {
        const [level, pathName] = path.split('-');
        paths = [...paths, pathName];
        const file = findFile(rootFile, paths) as FileItemProps;
        const generatedId = generateNodeId(file, parseInt(level));
        ids = [...ids, generatedId];
      });
    setDefaultExpanded(ids);
    onPathSelected && onPathSelected(paths);
  }, [pathList]);

  const filterRootPath = (paths: string[]): string[] => {
    return paths.filter(
      item =>
        decodeFileName(item) !== decodeFileName(rootPathName) &&
        decodeFileName(item) !== decodeFileName('1-' + rootPathName)
    );
  };

  useEffect(() => {
    const filteredPath = filterRootPath(currentPath);
    setPathList(filteredPath.map((path, i) => `${i + 2}-${path}`));
  }, [currentPath]);

  const renderTree = (nodes: FileItemProps, id: number) => (
    <TreeItem
      classes={{
        content: 'file-system-navigator__content',
        label: 'file-system-navigator__label',
      }}
      key={generateNodeId(nodes, id)}
      nodeId={generateNodeId(nodes, id)}
      label={nodes.name}
    >
      {Array.isArray(nodes.files)
        ? nodes.files
            .filter(filterTreeItem)
            .map(node => renderTree(node, id + 1))
        : null}
    </TreeItem>
  );

  const handleNodeSelect = (
    event: React.SyntheticEvent,
    nodeIds: Array<string> | string
  ) => {
    if (!Array.isArray(nodeIds)) {
      const { level, name } = JSON.parse(nodeIds);
      const pathName = `${level}-${name}`;
      const newPaths = filterRootPath([
        ...pathList.filter(path => path.split('-')[0] < level),
        pathName,
      ]).sort();

      setPathList(newPaths);
    }
  };

  return (
    <TreeView
      aria-label="file system navigator"
      defaultCollapseIcon={<ArrowDropDownIcon />}
      defaultExpandIcon={<ArrowRightIcon />}
      expanded={defaultExpanded}
      onNodeSelect={handleNodeSelect}
      sx={style}
    >
      {renderTree(rootFile, 1)}
    </TreeView>
  );
};

export default FileSystemNavigator;
